import { useContext, useState } from "react";
import { useLocalization } from "@fluent/react";
import { modules, entities } from "byzantine";
import {
  Alert,
  Row,
  TextInput,
  Select,
  ResponsiveFlex,
  Tooltip,
} from "@narmi/design_system";
import {
  LimitsForDisplayType,
  MAX_MEMO_LENGTH,
  useMappedEntryDescription,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { MAX_BULK_TITLE_LENGTH } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import Account from "byzantine/src/Account";
import * as formAdapters from "../../../src/adapters";
import ComboAccountSelector from "../../ComboAccountSelector";
import AccountContext from "../../contexts/AccountContext";
import LimitsAndDisclosuresDialog from "../../transfer/ach/LimitsAndDisclosuresDialog";

type TemplateFieldsProps = {
  fromAccount: boolean;
  secCode: boolean;
  entryDesc: boolean;
  description: boolean;
};

const PaymentDetailsSection = ({
  limits,
  templateFields,
}: {
  limits: LimitsForDisplayType;
  templateFields?: TemplateFieldsProps;
}) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext);
  const [showLimitsDialog, setShowLimitsDialog] = useState(false);
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const codes = modules.achPayments.useSECCodes();
  const { mappedOptions } = useMappedEntryDescription();

  const validAccounts = accounts.filter((account: Account) =>
    account.isValidAchTransferSource()
  );

  const toggleLimitsDialog = () => {
    setShowLimitsDialog(!showLimitsDialog);
  };

  return (
    <>
      <div>
        <h4 className="fontSize--l nds-sans margin--top--xl margin--bottom--xxs">
          {l10n.getString(
            templateFields
              ? "bulk-template-additional-details-title"
              : "ach-payment-payment-details-title"
          )}
        </h4>
        <p className="margin--bottom--l">
          {l10n.getString(
            templateFields
              ? "bulk-template-additional-details-description"
              : "bulk-payment-details-description"
          )}
        </p>
        <ResponsiveFlex gapSize="l">
          <Row>
            <Row.Item>
              <TextInput
                label={l10n.getString("bulk-payment-title")}
                maxLength={MAX_BULK_TITLE_LENGTH}
                endContent={
                  <Tooltip
                    text={l10n.getString("bulk-payment-title-tooltip-copy")}
                    maxWidth="235px"
                  >
                    <span className="narmi-icon-info"></span>
                  </Tooltip>
                }
                {...formAdapters.input(form, "title")}
              />
            </Row.Item>
          </Row>

          {/* account selector: hidden when template provides it (shown in TemplateDetailsCard) */}
          {!templateFields?.fromAccount && (
            <div className="margin--bottom--l">
              <Row>
                <Row.Item>
                  {validAccounts.length === 0 ? (
                    <>
                      <div className="margin--bottom--s">
                        <Alert isActive kind="warn" isDismissable={false}>
                          {l10n.getString(
                            "bulk-payment-no-valid-source-account-alert"
                          )}
                        </Alert>
                      </div>
                      <TextInput
                        label={l10n.getString("label-account")}
                        disabled
                      />
                    </>
                  ) : (
                    <ComboAccountSelector
                      label={l10n.getString("label-account")}
                      accounts={validAccounts}
                      isDestination={true}
                      showAddExternalAccountLink={false}
                      {...formAdapters.select(form, "fromAccount")}
                    />
                  )}
                </Row.Item>
              </Row>
            </div>
          )}

          {/* SEC Code: hidden when template provides it (shown in TemplateDetailsCard) */}
          {!templateFields?.secCode && (
            <div className="margin--bottom--l">
              <Row>
                <Row.Item>
                  <Select
                    label={l10n.getString("label-payment-type")}
                    {...formAdapters.select(form, "secCode")}
                  >
                    {codes.map((code, index) => (
                      <Select.Item key={`account_type_${index}`} value={code}>
                        {code}
                      </Select.Item>
                    ))}
                  </Select>
                </Row.Item>
              </Row>
            </div>
          )}

          {/* entry description: hidden when template provides it (shown in TemplateDetailsCard) */}
          {!templateFields?.entryDesc && (
            <div className="margin--bottom--l">
              <Row>
                <Row.Item>
                  <Select
                    label={l10n.getString("label-entry-description")}
                    {...formAdapters.select(form, "entryDesc")}
                  >
                    {mappedOptions.map(({ display, value }) => (
                      <Select.Item key={value} value={value}>
                        {display}
                      </Select.Item>
                    ))}
                  </Select>
                </Row.Item>
              </Row>
            </div>
          )}

          {/* internal Note: hidden when template provides it (shown in TemplateDetailsCard) */}
          {!templateFields?.description && (
            <Row>
              <Row.Item>
                <TextInput
                  label={l10n.getString("label-internal-note-optional")}
                  maxLength={MAX_MEMO_LENGTH}
                  {...formAdapters.input(form, "description")}
                />
              </Row.Item>
            </Row>
          )}
        </ResponsiveFlex>
        <LimitsAndDisclosuresDialog
          isOpen={showLimitsDialog}
          handleClose={toggleLimitsDialog}
          limits={limits}
          fromFlow="bulkPayment"
        />
      </div>
    </>
  );
};

export default PaymentDetailsSection;
